/**
 * Feature-key → module-type mapping (ADR 042, WEB-5; default-deny — Phase 7)
 *
 * Decides which workspace module types are GATEABLE by the entitlement service.
 * Under default-deny (ADR §13) every module type is catalogued, so the feature
 * key for a module IS its (canonical) type string — the map is the identity over
 * the catalogued types. A module type ABSENT from this map is NOT a catalogued
 * module (e.g. the internal `sidebar_settings` scaffold, or a plugin id like
 * `app-analytics`); it has no feature key and renders unconditionally — there is
 * nothing to gate.
 *
 * The catalog mirrors `MODULE_TYPES` in `@xavior/domain`
 * (`services/workspace-bootstrap.ts`) — web can't import domain, so it is
 * duplicated here. Keep the two in sync: a new catalogued module type must be
 * added in both places (the domain `guard:module-catalog` keeps the server side
 * honest; this is its FE mirror).
 */
import { MODULE_ALIASES } from '@/types/modules'
import type { EntitlementMap, EntitlementVerdict } from '@/lib/api/endpoints/entitlements'

/**
 * Every catalogued module type, keyed by its CANONICAL type (post-`MODULE_ALIASES`).
 * The feature key === the type, so this is an identity map. Mirrors the domain
 * `PUBLIC_MODULE_TYPES` + `INTERNAL_MODULE_TYPES` catalog.
 *
 * Note on aliases: {@link featureKeyForModuleType} runs `MODULE_ALIASES` first, so
 * a view-variant collapses to its catalogued parent before this lookup (e.g.
 * `gmail`→`email`, `photos`/`docs`/`gallery`→`drive`, `stat_list`→`health`). The
 * aliased-away types are still listed here for parity with the domain catalog;
 * they are simply never reached as a lookup key.
 */
export const FEATURE_BY_MODULE_TYPE: Record<string, string> = Object.fromEntries(
  [
    // ── Public (the launch baseline — `individual` profile) ──
    'email',
    'contact_list',
    'event_list',
    'drive',
    'file_viewer',
    'docs',
    'note_grid',
    'photos',
    'gallery',
    'task_list',
    'pages',
    'report',
    'html_viewer',
    // ── Internal (catalogued, ungranted at launch — admin-grantable) ──
    'automations', // canonical module type (renamed from legacy agents-type, migration 076)
    'synthesis',
    'places',
    'place_list',
    'notes',
    'health',
    'channels',
    'stat_list',
    'stat_chart',
    'client_list',
    'news',
    'mixpanel',
    'shopify-crm',
    'campaigns',
  ].map((type) => [type, type]),
)

/**
 * The feature key gating a given module type, or `undefined` when the type is
 * NOT catalogued (and therefore renders unconditionally — there is nothing to
 * gate). The `featureMap` parameter is injectable for tests; production callers
 * omit it. `MODULE_ALIASES` canonicalizes a view-variant to its catalogued
 * parent first, so e.g. a `gmail` module resolves the `email` feature.
 */
export function featureKeyForModuleType(
  moduleType: string,
  featureMap: Record<string, string> = FEATURE_BY_MODULE_TYPE,
): string | undefined {
  const canonical = MODULE_ALIASES[moduleType] ?? moduleType
  return featureMap[canonical]
}

/**
 * The entitlement verdict for a module type under DEFAULT-DENY (ADR §13, Phase 7).
 *
 * - Un-catalogued type (no feature key) → `granted`: there is nothing to gate
 *   (e.g. the `sidebar_settings` scaffold).
 * - Map still loading (`entitlements === undefined`) → `granted`: a granted
 *   module must never flash-hide, and the authoritative `GET /modules` filter has
 *   already dropped any ungranted module from the list this is read against
 *   (WEB-4).
 * - Map loaded → the verdict for the key, defaulting a MISSING key to `hidden`
 *   (default-deny: a catalogued module the projection didn't grant is concealed).
 */
export function verdictForModuleType(
  moduleType: string,
  entitlements: EntitlementMap | undefined,
  featureMap: Record<string, string> = FEATURE_BY_MODULE_TYPE,
): EntitlementVerdict {
  const featureKey = featureKeyForModuleType(moduleType, featureMap)
  if (!featureKey) return 'granted'
  if (!entitlements) return 'granted'
  return entitlements[featureKey] ?? 'hidden'
}
